import { Injectable } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, SilentRequest } from '@azure/msal-browser';
import { BaseAuthService, BaseUser } from '@shared/utils/base-auth';
import { Subject, filter, takeUntil, Observable, of, throwError, catchError, map, shareReplay, tap, lastValueFrom } from 'rxjs';
import { B2cConfig } from '../config/b2c-config';
import { OfficerDetailsBaseService } from '@shared/utils/officer';

@Injectable({
  providedIn: 'root',
})
export class BaseB2cAuthService implements BaseAuthService {
  private readonly destroying$ = new Subject<void>();
  private tokenCache$?: Observable<string>;

  public constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private b2cConfig: B2cConfig,
    private officerDetailsService: OfficerDetailsBaseService
  ) {}

  public async getToken(): Promise<string> {
    if (!this.tokenCache$) {
      const account = this.authService.instance.getActiveAccount();
      if (!account) {
        throw new Error('No active account found');
      }

      const request: SilentRequest = {
        scopes: [...this.b2cConfig.b2cScopes],
        account: account,
      };

      this.tokenCache$ = this.authService.acquireTokenSilent(request).pipe(
        tap((response) => {
          if (!response.accessToken) {
            throw new Error('Failed to retrieve access token');
          }
        }),
        map((response) => response.accessToken),
        catchError((error) => throwError(() => error)),
        shareReplay(1)
      );
    }

    return lastValueFrom(this.tokenCache$ ?? of(''));
  }

  public initializeAuth(): void {
    this.authService.handleRedirectObservable().subscribe();

    this.msalBroadcastService.msalSubject$.subscribe((response) => {
      if (response.error && (response.error.message.includes('AADB2C90091') || response.error.message.includes('AADSTS700084'))) {
        this.authService.loginRedirect();
      }
    });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  public getCurrentUser(): BaseUser | undefined {
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount) {
      return undefined;
    }

    if (!activeAccount.idTokenClaims) {
      return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const idTokenClaims = activeAccount.idTokenClaims as any;

    const email = idTokenClaims?.email ?? idTokenClaims?.preferred_username ?? idTokenClaims['signInNames.emailAddress'];
    const firstName = idTokenClaims?.given_name ?? idTokenClaims?.name;

    return new BaseUser(email, idTokenClaims?.sub, '', firstName, idTokenClaims?.family_name);
  }

  public getCurrentBaseUser(): Observable<BaseUser | undefined> {
    return of(this.getCurrentUser());
  }

  public logout(): void {
    this.authService.logoutRedirect();
  }

  public onDestroy(): void {
    this.destroying$.next(undefined);
    this.destroying$.complete();
  }

  private checkAndSetActiveAccount(): void {
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);

      this.officerDetailsService.fetchCurrentOfficer();
    }
  }
}
